/**
 * Curated prompt library for Heist's MCP integration.
 *
 * Single source of truth — consumed by both the public marketing page
 * (mcpFeatures.js) and the in-app help drawer (helpDrawer.js). Phase 3
 * of the UX audit moved this here so the two surfaces never drift.
 *
 * Each entry: { title, prompt, why } where `why` is one line on what
 * makes this useful. Tone: short, dry, no buzzwords.
 */

/**
 * The public MCP endpoint URL — the value users paste into their AI
 * chat's Connectors. Always the production origin because that's the
 * only URL external AI services (Claude.ai, ChatGPT, etc.) can reach.
 * Imported by setupWizard.js / connectors.js / mcpFeatures.js so every
 * surface shows the same URL regardless of environment (local dev,
 * preview deploys, staging — none of those are valid paste targets).
 */
export const MCP_PUBLIC_URL = 'https://app.heistbrain.com/mcp';
export const PROMPTS = [
    {
        category: 'Plan a campaign',
        items: [
            {
                title: 'Two-week launch campaign',
                prompt: 'Plan a 2-week LinkedIn + X launch campaign for [my product]. 3 posts per week per platform, mix of educational and promotional, schedule weekday mornings. Use my brand voice.',
                why: '12 drafts grouped under one campaign in one tool call. Auto-scheduled.',
            },
            {
                title: 'Holiday content calendar',
                prompt: 'Plan a holiday content calendar from Black Friday through New Year. Daily Instagram + 3x weekly LinkedIn. Mix promotional + behind-the-scenes + customer stories.',
                why: 'One conversation produces a 35-day calendar. Bulk schedule, then walk away.',
            },
            {
                title: 'Weekly thought-leadership',
                prompt: 'Plan next week of LinkedIn posts. 5 thought-leadership pieces — no product pitch, just opinions and lessons. Pull tone from my last high-performer.',
                why: 'Heist reads your top-performing post and matches its voice automatically.',
            },
            {
                title: 'Spreadsheet → scheduled calendar',
                prompt: 'I have a spreadsheet of content ideas (paste the rows here or share the file). For each row, draft the post in my brand voice, pick the right platform, and add it to my Heist calendar. Confirm each one with me before scheduling.',
                why: 'Turns a planning spreadsheet into a filled calendar without a manual data-entry session.',
            },
            {
                title: 'Launch campaign with weekly themes',
                prompt: 'I\'m launching [product] on [date]. Build me a 4-week campaign in Heist leading up to it: week 1 awareness, week 2 problem framing, week 3 solution + social proof, week 4 hard push. 3 posts per week per platform. Use my brand voice and schedule across my connected accounts.',
                why: 'A full 4-week launch sequence, themed and scheduled, in one conversation.',
            },
        ],
    },
    {
        category: 'Repurpose a winner',
        items: [
            {
                title: 'One post → five platforms',
                prompt: 'Take my last LinkedIn post that performed well and create five platform-tailored versions for Instagram, X, Facebook, TikTok script, and a newsletter intro.',
                why: 'Same idea, five formats, each with the right length and structure for that platform.',
            },
            {
                title: 'Blog post → social cascade',
                prompt: 'Find my most recent blog post and turn it into 8 social posts: 3 for LinkedIn, 3 for X, 2 for Instagram. Tease the blog and link to it.',
                why: 'Promotes the blog without you having to draft each tease post by hand.',
            },
            {
                title: 'Variations from a top performer',
                prompt: 'Take my best-performing post from the last 90 days. Generate 5 variations with different hooks but the same core message. I want to A/B test next month.',
                why: 'Heist already tracks performance. The agent picks the winner and remixes it.',
            },
            {
                title: 'Audit last 30 posts → 5 fresh drafts',
                prompt: 'Look at my last 30 posts in Heist. Tell me which formats / hooks / topics performed best and which fell flat. Then draft 5 new posts that lean into what\'s working, and save them to my library.',
                why: 'Performance review + new drafts in one pass, grounded in your own data.',
            },
        ],
    },
    {
        category: 'Edit + republish',
        items: [
            {
                title: 'Add an FAQ to a live blog',
                prompt: 'Find my pricing blog post, add an FAQ section at the bottom answering common pricing questions, and republish to WordPress.',
                why: 'Edits land in WordPress without you opening the WP editor.',
            },
            {
                title: 'Bulk update old drafts',
                prompt: 'Find all my draft blog posts older than 30 days and add a CTA paragraph to each one promoting our newsletter signup.',
                why: 'Bulk update with a dryRun preview before committing.',
            },
            {
                title: 'Refresh a stale post',
                prompt: 'Pull up my "How to Use Heist" blog. Update the tool count to 112 and re-publish.',
                why: 'Specific, surgical edits with a single instruction.',
            },
        ],
    },
    {
        category: 'Brand brain + voice',
        items: [
            {
                title: 'Audit voice consistency',
                prompt: 'Read my last 10 posts and tell me where my voice drifts. Are there any "AI-flavored" sentences I should rewrite?',
                why: 'Heist scores every post against your brand voice and flags drift.',
            },
            {
                title: 'Add a fact to the Brain',
                prompt: 'Remember this for future content: my company won the 2026 SaaS Innovation Award in March. Add it as a memory fact.',
                why: 'Facts persist forever. Every future draft can reference them.',
            },
            {
                title: 'Define a "do not say" rule',
                prompt: 'Add a rule to my Brain: never use the word "leverage" in my posts. It sounds corporate.',
                why: 'Server-side guardrails. The agent will refuse to ship a draft that violates it.',
            },
        ],
    },
    {
        category: 'Walk back mistakes',
        items: [
            {
                title: 'Undo a bulk delete',
                prompt: 'I just bulk-deleted 14 drafts I shouldn\'t have. Find the action and revert it.',
                why: 'Heist\'s audit log makes most writes reversible — just ask the agent to undo it.',
            },
            {
                title: 'Restore an old draft',
                prompt: 'Yesterday I edited my pricing blog post and I want to roll it back to the version before that edit.',
                why: 'The agent finds the agent_action and restores the before-snapshot.',
            },
            {
                title: 'Remove a fact you added wrong',
                prompt: 'I added a memory fact about a discount code that\'s no longer valid. Find it and remove it.',
                why: 'Brain stays accurate over time without manual cleanup sessions.',
            },
        ],
    },
];

/**
 * Page route → prompt-category mapping. Used by the help drawer to surface
 * a contextually-relevant prompt for whatever page the user is on. Not all
 * routes need a hint — the drawer falls back to "Plan a campaign" when a
 * route isn't listed here.
 */
export const ROUTE_TO_CATEGORY = {
    home: 'Plan a campaign',
    memory: 'Brand brain + voice',
    create: 'Repurpose a winner',
    library: 'Edit + republish',
    calendar: 'Plan a campaign',
    campaigns: 'Plan a campaign',
    analytics: 'Brand brain + voice',
    connectors: 'Brand brain + voice',
    'agent-inbox': 'Walk back mistakes',
};

/**
 * Page-tailored "plan this with your AI chat" prompts.
 *
 * Phase 3d: replaced the category-pick (which surfaced a random prompt
 * from the broader PROMPTS library) with a single, MCP-planning-focused
 * prompt per page. Every prompt frames Claude/ChatGPT as the way to
 * plan/do the thing on the current page — that's the user-facing point
 * of the MCP integration.
 *
 * Tone: imperative + specific. The user copy-pastes this into their AI
 * chat verbatim and the agent does the work via MCP tools. Minimal
 * placeholders so they can paste-and-go.
 */
export const ROUTE_PLAN_PROMPTS = {
    home: {
        title: 'Plan my next two weeks',
        prompt: 'Plan my next two weeks of content using Heist. Ask me about my goals, audience, and what I want to push — then generate the drafts and schedule them across my connected platforms. Use my brand voice.',
        why: 'One conversation produces a 14-day plan, drafts, and a filled calendar.',
    },
    memory: {
        title: 'Build out my Brand Brain',
        prompt: "Help me set up my Heist brand brain. Walk me through mission, voice, audience, offers, and CTA — one question at a time. Suggest options based on my answers and my website if I share the URL. Save each piece to Heist as we go.",
        why: 'Better brain = sharper drafts. The agent fills facts, voice, and audience for you.',
    },
    create: {
        title: 'Turn this idea into a week of content',
        prompt: 'Take the topic I give you and turn it into one week of content. Mix it up: 3 LinkedIn posts, 3 X posts, 2 Instagram posts, and ONE 60-second TikTok faceless video (script → voice → slideshow MP4). Generate an AI featured image for any blog version. Match the voice from my best-performing recent post. Use my brand brain. Land all the drafts in my Heist library.',
        why: 'Skip the one-at-a-time loop. One prompt → a week of posts + a video + AI images.',
    },
    library: {
        title: 'Find my winner and remix it',
        prompt: 'Find my best-performing post from the last 60 days. Generate 5 variations with different hooks and angles, plus a long-form blog version. Save them all as drafts.',
        why: 'Heist already tracks performance. Let the agent find the winner and multiply it.',
    },
    calendar: {
        title: 'Fill the next month of the calendar',
        prompt: "Plan my next month of content. 3 posts per week across my connected platforms — mix of educational, promotional, and behind-the-scenes. Schedule them weekday mornings. Use my brand voice.",
        why: 'A full month of scheduled content from one prompt. Walk away.',
    },
    campaigns: {
        title: 'Spin up a launch campaign',
        prompt: 'Plan a 2-week launch campaign for the product/topic I describe. 12 posts total (3 per week per platform), mix of teaser → educational → promotional → social proof. Auto-schedule.',
        why: '12 drafts grouped under one campaign in one tool call.',
    },
    analytics: {
        title: 'Audit voice + spot drift',
        prompt: 'Read my last 10 posts and tell me where my voice drifts. Are there "AI-flavored" sentences I should rewrite? Suggest specific fixes, then update the drafts.',
        why: 'Heist scores every post against your brand voice — let the agent find the drift.',
    },
    connectors: {
        title: 'Set up my brand profile via chat',
        prompt: "Help me set up my Heist brand profile. Ask me one question at a time — mission, voice, audience, goals, offers. If I don't know an answer, suggest options based on what we've discussed. I can also share my website URL or upload brand documents.",
        why: 'The fastest way to fill the Brain — let the AI walk you through it.',
    },
    'agent-inbox': {
        title: 'Review and approve queued drafts',
        prompt: 'Review the drafts in my Heist Agent Inbox. For each one, tell me if it matches my brand voice, then approve, edit, or reject. Schedule the approved ones.',
        why: 'Bulk-review a batch of agent-generated drafts in one pass.',
    },
};

/**
 * Look up a (deterministic) prompt to suggest for a given route.
 *
 * Phase 3d: prefers ROUTE_PLAN_PROMPTS (one curated MCP-planning prompt
 * per page) over the legacy category-pick. Falls back to the category
 * pick when the route isn't in the new map, so the broader PROMPTS
 * library stays available for marketing surfaces (mcpFeatures.js).
 */
export function pickPromptForRoute(route, opts = {}) {
    const planPrompt = ROUTE_PLAN_PROMPTS[route];
    if (planPrompt && !opts.random) {
        return { category: 'Plan with your AI chat', ...planPrompt };
    }
    const category = ROUTE_TO_CATEGORY[route] || 'Plan a campaign';
    const group = PROMPTS.find(g => g.category === category) || PROMPTS[0];
    const idx = opts.random
        ? Math.floor(Math.random() * group.items.length)
        : 0;
    return { category, ...group.items[idx] };
}

/**
 * Total tool count surfaced in marketing copy. Single source of truth
 * for both /#mcp-features and /#help?tab=mcp so the number doesn't
 * drift between pages when we add tools.
 */
export const MCP_TOOL_COUNT = 112;

/**
 * Tool inventory — grouped for readability, not the full list. Each row
 * says what the user can do with that group. Phase 4 (in-app MCP page)
 * moved this here from mcpFeatures.js so the help-center MCP tab
 * renders the same nine cards.
 */
export const MCP_TOOL_GROUPS = [
    { name: 'Drafts',           count: '15+', desc: 'Create, update, schedule, publish, bulk-create up to 50, find + replace across drafts, AI-enhance, generate variations, one-call image/video drafts from any AI source.' },
    { name: 'Blog posts',       count: '14+', desc: 'Generate full blog posts, sanitize HTML, attach media, manage tags, publish to WordPress, republish edits, soft-delete + restore.' },
    { name: 'Snippets',         count: '8',   desc: 'Reusable HTML blocks (CTAs, FAQs, signatures) — global or per-post, attach/detach for surgical control.' },
    { name: 'Brand brain',      count: '8+',  desc: 'Memory facts, knowledge documents, URL sources, brand profile, personas — read or write with audit trail.' },
    { name: 'Calendar + schedule', count: '5+', desc: 'Scheduled feed, drag-to-reschedule, fill-calendar-range, bulk schedule up to 50 drafts at once.' },
    { name: 'Media + stock',    count: '13+', desc: 'Upload images / videos / PDFs / DOCX from URL or base64. Stock + library search, attach/detach, generate AI images, and curate stock (policy + blocklist).' },
    { name: 'Video',            count: '5',   desc: 'Faceless videos end-to-end: list voices, generate a voiceover, compose slideshow MP4s with captions, or run the whole script-to-video pipeline in one call.' },
    { name: 'Autopilot',        count: '8',   desc: 'Read autopilot state, propose + apply plan changes, pick topics, seed a cycle, send the veto email, pause/resume — your weekly content engine, driven from chat.' },
    { name: 'Campaigns',        count: '5',   desc: 'Create campaigns, link drafts retroactively, generate variations across a campaign, read campaign status.' },
    { name: 'Audit + revert',   count: '2',   desc: 'Heist logs every action it takes. Made a mistake? Ask the agent to undo it — most writes (deletes, edits, fact changes, even bulk ops) come back.' },
    { name: 'Meta',             count: '8',   desc: 'Ask the agent what it can do, what permissions it has, which platforms are connected (including your Pinterest boards), or revisit a prior conversation thread — the meta-tools that orient any new chat session.' },
];

/**
 * "Tips for talking to Heist" — best-practice rules. Phase 4 moved
 * these from mcpFeatures.js so the in-app help-center MCP tab can
 * render the same set without copy/paste drift.
 */
export const MCP_TIPS = [
    {
        title: 'Start with "what can you do?"',
        body: 'On a fresh chat, this prompts the agent to lay out everything it can do for you — what tools it has, what permissions, where to start. 30-second orientation that saves a lot of trial and error.',
    },
    {
        title: 'Be specific about platforms and counts',
        body: '"Plan a 2-week LinkedIn campaign — 3 posts per week" beats "make me some content." The agent uses Heist\'s platform rules to format each post correctly (char limits, hashtag conventions, best posting times) — give it the constraints and it does the rest.',
    },
    {
        title: 'Reference your top performer',
        body: '"Match the voice of my best LinkedIn post from the last 60 days" pulls your actual high-performer and grounds the new drafts in its tone. Better than "be casual" or "be professional" — it\'s your real voice, scored by your real audience.',
    },
    {
        title: 'Use approval mode wisely',
        body: 'Brand-Brain writes (facts, rules, document uploads) queue for approval by default — they shape every future draft, so you want a human gate. Flip Settings → AI → Brain-write approval to <strong>instant</strong> when you trust the agent. Drafts go straight to your library either way.',
    },
    {
        title: 'Reserve "publish" scope for automation you trust',
        body: 'When you\'re first exploring, the OAuth flow grants <code>read</code>, <code>draft</code>, <code>schedule</code>. Drafts land in your library, you review, you publish. Only grant <code>publish</code> when you\'re comfortable letting the agent push to Meta / X / LinkedIn / WordPress without you in the loop.',
    },
    {
        title: 'When in doubt, dryRun first',
        body: 'Bulk operations support a preview mode. Ask the agent to "preview the batch first" before it actually writes anything — it validates each item (brand access, content limits, your rules) and reports back without committing. You decide whether to ship it.',
    },
    {
        title: 'Mistakes are reversible',
        body: 'Every action Heist takes is logged. If you said "delete those 14 drafts" and regretted it, ask the agent to revert it — Heist brings them back. Most actions are reversible (creates, deletes, updates, fact writes). Publishes already hit the public internet, so for those, ask the agent to republish with the correction.',
    },
];

/**
 * FAQ entries for both the marketing page and the in-app help center.
 * Phase 4 moved from mcpFeatures.js. `a` may contain inline HTML
 * (<code>, <strong>) — both surfaces render via innerHTML.
 */
export const MCP_FAQ = [
    {
        q: 'Is this an API I have to write code against?',
        a: 'No. You paste one URL into your AI chat\'s Settings → Connectors and approve the OAuth screen. Heist\'s tools then appear inside the chat the same way Claude\'s built-in tools do. The "API" is your conversation.',
    },
    {
        q: 'Do I need an API key?',
        a: 'Not for Claude.ai or ChatGPT — those use the OAuth URL paste flow. API keys are only needed for stdio clients like Claude Desktop, Cursor, or Windsurf, which don\'t speak OAuth. The API key path is hidden under Advanced Settings on purpose; most users never touch it.',
    },
    {
        q: 'What\'s the difference between this and ChatGPT plugins?',
        a: 'MCP (Model Context Protocol) is the open standard that replaced plugins. It works across Claude, ChatGPT, Cursor, Windsurf, and any compliant AI client. Heist\'s server is the same regardless of which chat you use — switch chats, keep your tools.',
    },
    {
        q: 'Will my AI chat see my brand data?',
        a: 'Only the brands you explicitly grant on the OAuth consent screen. Heist returns brand brief, voice rules, recent posts, calendar, and platform rules to the AI when it asks — that\'s how the AI writes in your voice. The data stays on Heist\'s servers; the AI sees only the slices it needs for the current task.',
    },
    {
        q: 'Can I revoke access?',
        a: 'Yes. Heist → Settings → Connections → Connectors lists every active session. Click Revoke and the AI immediately loses access. Or revoke from inside Claude.ai / ChatGPT in their connector list.',
    },
    {
        q: 'What plan do I need?',
        a: 'Pro and above. Trial users can preview the marketing pages and the help guides. The OAuth endpoint and the underlying tools require a Pro subscription. Free 7-day trial available — no card required.',
    },
    {
        q: 'What happens if the agent makes a mistake?',
        a: 'Most writes are reversible. Every action stamps an <code>agent_actions</code> row in your audit log; ask the agent to revert by ID and Heist restores the previous state. Brand-Brain writes (facts, rules) queue for approval by default, so they need your sign-off before they take effect.',
    },
    {
        q: 'How does it know my brand voice?',
        a: 'You set up a Brand Brain in Heist — voice description, audience, offers, CTA preferences, words to avoid, plus uploadable PDFs / docs and memory facts. Every tool call grounds in that Brain. The more you put in, the better the agent matches your voice.',
    },
];
